/**
 * usePublicConfig — le seul fetch de `GET /api/public-config`.
 *
 * L'endpoint sert deux choses : les feature flags, et la POLITIQUE DE
 * MOT DE PASSE. `useFeatureFlags` le lisait déjà avec son propre cache ;
 * ajouter un second hook avec un second cache aurait fait deux requêtes
 * au montage de l'écran d'inscription, qui a besoin des deux. Le cache
 * et la requête en vol vivent donc ici, et les deux hooks publics en
 * dérivent.
 *
 * POURQUOI LA POLITIQUE VIENT DU SERVEUR
 * ---------------------------------------
 * Parce que la recopier ici est exactement ce qui l'avait laissée à 8
 * dans cinq fichiers pendant que le backend en exigeait autre chose. Un
 * formulaire qui annonce « min. 8 » et un serveur qui refuse à 11, c'est
 * un utilisateur qui ne comprend pas pourquoi on le refuse.
 *
 * Le repli ci-dessous n'est PAS une seconde définition de la règle :
 * c'est ce qu'on affiche pendant les 80 ms où la réponse n'est pas
 * arrivée, et si le réseau tombe. Il est volontairement identique à la
 * valeur serveur du jour ; s'il diverge un jour, c'est le serveur qui
 * tranche — il est seul à refuser.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Safe defaults: everything OFF until we hear otherwise. Components that
// gate on these must render `null` (or the pre-feature state) while we
// haven't confirmed the flag is on — no flashing of soon-to-hide UI.
const DEFAULT_FLAGS = {
  marketplace_unlock_enabled: false,
  credit_packs_enabled: false,
  // Billing of the attorney video consultation. Off means the session is
  // free and no price is shown anywhere — which is the shipped state.
  live_counsel_billing_enabled: false,
  // Le dépôt sans inscription. `false` en repli comme tous les autres :
  // tant qu'on n'a pas la réponse du serveur, les CTA publics pointent
  // sur l'inscription classique — le parcours éprouvé (cf.
  // hooks/useStartRoute.js).
  anon_intake_enabled: false,
  // La seule exception au « fermé par défaut » : la section témoignages
  // de la home fait partie de la page validée, le drapeau ne sert qu'à
  // l'éteindre. Allumée tant que le serveur n'a pas répondu, donc pas de
  // section qui apparaît après coup.
  home_testimonials_enabled: true,
};

// Miroir de `backend/constants/password_policy.public_password_policy()`.
// Repli d'affichage uniquement — cf. l'en-tête.
const DEFAULT_PASSWORD_POLICY = {
  min_length: 12,
  max_bytes: 72,
  requires_uppercase: false,
  requires_digit: false,
  requires_symbol: false,
  breach_checked: true,
};

// Contact WhatsApp — miroir de `backend/config/feature_flags.whatsapp_contact()`.
// Fermé par défaut, comme les flags : tant que la réponse n'est pas là (ou
// si elle ne vient pas), aucun bouton. Le numéro vide suffirait d'ailleurs
// à lui seul à ne rien afficher.
const DEFAULT_WHATSAPP = {
  show_whatsapp_contact: false,
  whatsapp_contact_number: '',
};

// Lien de dépôt d'avis — miroir de `backend/config/feature_flags.review_link()`.
// VIDE PAR DÉFAUT, et c'est la valeur qui compte : l'écran de célébration
// n'affiche son bloc « Content du résultat ? » que si l'URL existe. Tant
// qu'il n'y a pas de page d'avis Archer, il n'y a pas de bouton — plutôt
// que le bouton mort qu'une URL codée en dur aurait donné.
const DEFAULT_REVIEW_LINK = '';

const DEFAULTS = {
  flags: DEFAULT_FLAGS,
  passwordPolicy: DEFAULT_PASSWORD_POLICY,
  whatsapp: DEFAULT_WHATSAPP,
  reviewLink: DEFAULT_REVIEW_LINK,
  // `false` tant que la réponse n'est pas arrivée (ou si elle ne vient
  // pas). Les composants qui se CONTENTENT de masquer une surface n'en
  // ont pas besoin — un flag à `false` par défaut suffit. Il sert à
  // ceux qui doivent CHOISIR entre deux rendus : sans lui, la bascule
  // de la page dossier afficherait la V2 pendant 80 ms puis la
  // remplacerait par la V3, ce qui se voit.
  loaded: false,
};

// Module-level cache — the endpoint is public and its payload doesn't
// change per user, so one fetch per page load is enough. `inFlight` is
// shared so simultaneously-mounted hooks don't stampede the endpoint.
let cachedConfig = null;
let inFlight = null;

function fetchConfig() {
  if (cachedConfig) return Promise.resolve(cachedConfig);
  if (inFlight) return inFlight;
  inFlight = axios
    .get(`${API}/public-config`, { withCredentials: true })
    .then((res) => {
      const config = {
        flags: { ...DEFAULT_FLAGS, ...(res?.data?.flags || {}) },
        passwordPolicy: {
          ...DEFAULT_PASSWORD_POLICY,
          ...(res?.data?.password_policy || {}),
        },
        // Les deux clés WhatsApp sont au PREMIER niveau de la réponse,
        // pas dans `flags` : celui-ci est booléen par contrat côté
        // serveur et le numéro est une chaîne.
        whatsapp: {
          show_whatsapp_contact: res?.data?.show_whatsapp_contact === true,
          whatsapp_contact_number: String(res?.data?.whatsapp_contact_number || '').trim(),
        },
        // Même raison, même niveau : une chaîne ne voyage pas dans `flags`.
        reviewLink: String(res?.data?.review_link_url || '').trim(),
        loaded: true,
      };
      cachedConfig = config;
      return config;
    })
    .catch(() => {
      // Network / server error → stay closed on the flags, keep the
      // documented policy for display. Do NOT cache so the next mount
      // can retry.
      //
      // `loaded: true` MALGRÉ l'échec, et c'est voulu : la question à
      // laquelle il répond est « la réponse est-elle arrivée ? », pas
      // « a-t-elle réussi ? ». Le laisser à `false` figerait pour
      // toujours l'écran d'attente de qui arbitre entre deux rendus,
      // alors que la bonne conduite en cas de panne est exactement la
      // même qu'avec les drapeaux fermés : rendre l'état d'avant.
      return { ...DEFAULTS, loaded: true };
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

function usePublicConfig() {
  const [config, setConfig] = useState(cachedConfig || DEFAULTS);

  useEffect(() => {
    let mounted = true;
    fetchConfig().then((c) => {
      if (mounted) setConfig(c);
    });
    return () => {
      mounted = false;
    };
  }, []);

  return config;
}

/** Les feature flags seuls — signature inchangée pour ses appelants. */
export function useFeatureFlags() {
  return usePublicConfig().flags;
}

/**
 * La politique de mot de passe telle que le SERVEUR l'applique :
 * `{ min_length, max_bytes, requires_uppercase, requires_digit,
 *    requires_symbol, breach_checked }`.
 */
export function usePasswordPolicy() {
  return usePublicConfig().passwordPolicy;
}

/**
 * Le contact WhatsApp public :
 * `{ show_whatsapp_contact, whatsapp_contact_number }`.
 *
 * Même canal, même cache que les flags et la politique de mot de passe —
 * un troisième hook avec sa propre requête aurait fait deux appels au
 * montage d'une page publique.
 */
export function useWhatsAppContact() {
  return usePublicConfig().whatsapp;
}

/**
 * L'URL de dépôt d'avis, ou la chaîne vide.
 *
 * Vide n'est pas un cas dégradé : c'est l'état livré tant que la page
 * d'avis Archer n'existe pas. Le seul appelant (l'écran de célébration
 * de la clôture) ne rend RIEN — ni titre, ni bouton, ni « Plus tard » —
 * quand elle est vide.
 */
export function useReviewLink() {
  return usePublicConfig().reviewLink;
}

/**
 * `true` dès que `/api/public-config` a répondu (ou a échoué et qu'on
 * s'est rabattu sur les valeurs fermées). À n'utiliser que pour ARBITRER
 * entre deux rendus — masquer une surface se fait avec le flag seul.
 */
export function usePublicConfigLoaded() {
  return usePublicConfig().loaded === true;
}

export { DEFAULT_PASSWORD_POLICY };
export default usePublicConfig;
